import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  Textarea,
  VStack,
  chakra,
} from "@chakra-ui/react";
import { useContext, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import {
  isValidPhoneNumber,
  validatePhoneNumberLength,
} from "libphonenumber-js";
import { ErrorMessage } from "@hookform/error-message";
import { phoneErrors } from "./GuestlistUserEditRow";
import { KYDGuestlistDisplayState } from "@common/interfaces/KYDEvent";
import { KYDUserWithGuestlist } from "../../../../actions";
import { createDialogContext, DialogProvider } from "../../../../Common/Dialog";
import { isTicketTypeSystem } from "../../../../utils";
import { CreatableSelect } from "chakra-react-select";

export const GuestlistAddUserDialogContext = createDialogContext();

const GuestlistAddEditUserDialogMobile = ({
  onSave,
  onClose,
  allocation,
  isLoading = false,
  getTicketTypeName,
  guestlistUser,
  creatingLoading,
  isEdit = false,
}: {
  isLoading: boolean;
  isEdit?: boolean;
  allocationErrors: Record<string, boolean | string | null>;
  allocation?: KYDGuestlistDisplayState;
  guestlistUser?: KYDUserWithGuestlist;
  getTicketTypeName: (id: string) => string;
  onClose: () => void;
  onSave: (
    data: {
      id?: string;
      guestlist_first_name: string;
      guestlist_last_name: string;
      phone_number: string;
      quantity: number;
      ticket_type_id: string;
    },
    mode: "create" | "edit"
  ) => Promise<void>;
  creatingLoading: boolean;
}) => {
  const { isOpen } = useContext(GuestlistAddUserDialogContext);

  const isCsvImportInProgress = useMemo(() => {
    if (!allocation) return false;

    const systemAllocation = allocation.allocations.find((a) =>
      isTicketTypeSystem(a.ticket_type_id)
    );

    if (systemAllocation) {
      return systemAllocation.is_processing;
    }
  }, [allocation]);

  const hasSystemAllocation = allocation?.allocations.some((a) =>
    isTicketTypeSystem(a.ticket_type_id)
  );

  const allocationsWithAvailableTickets = allocation.allocations
    .filter((a) => {
      const isPreviouslySelectedTicketType =
        guestlistUser?.guestlist.ticket_type_id === a.ticket_type_id;

      return (
        a.remaining > 0 ||
        (isEdit && isPreviouslySelectedTicketType) ||
        (!isEdit &&
          isTicketTypeSystem(a.ticket_type_id) &&
          !isCsvImportInProgress)
      );
    })
    .concat(
      hasSystemAllocation
        ? []
        : [
            {
              allocation: 0,
              assigned: 0,
              remaining: 0,
              ticket_type_id: "SS",
              is_processing: false,
            },
          ]
    )
    .sort((a, b) => {
      if (isTicketTypeSystem(a.ticket_type_id)) {
        return 1;
      }

      return -1;
    });

  const defaultCreateRow = useMemo(
    () => ({
      id: guestlistUser ? guestlistUser.id : undefined,
      guestlist_first_name: guestlistUser ? guestlistUser.first_name : "",
      guestlist_last_name: guestlistUser ? guestlistUser.last_name : "",
      phone_number: guestlistUser ? guestlistUser.phone_number : "",
      quantity: guestlistUser ? guestlistUser.guestlist.assigned : 1,
      ticket_type_id: guestlistUser
        ? guestlistUser.guestlist.ticket_type_id
        : allocationsWithAvailableTickets.at(0)?.ticket_type_id,
      notes: guestlistUser ? guestlistUser.notes : "",
      tags: guestlistUser ? guestlistUser.tags : [],
    }),
    [guestlistUser, allocationsWithAvailableTickets]
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid, errors },
  } = useForm({
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
    values: defaultCreateRow,
  });

  const selectedTicketTypeId = watch("ticket_type_id");
  const selectedAllocation = allocation?.allocations?.find(
    (a) => a.ticket_type_id === selectedTicketTypeId
  );

  const isSystemTicketType = !!selectedTicketTypeId?.startsWith("SS");

  const _onClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={_onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          mb={[0, "auto"]}
          borderBottomLeftRadius={[0, "md"]}
          borderBottomRightRadius={[0, "md"]}
        >
          <ModalHeader paddingTop={4}>
            <VStack gap={0} width={"full"}>
              <Text w="full">Add to guestlist</Text>
            </VStack>
          </ModalHeader>
          <ModalBody textAlign={"left"}>
            <VStack spacing={4} width="full">
              <HStack>
                <Box>
                  <Text w="full">First Name</Text>
                  <Input
                    placeholder="Name"
                    key={guestlistUser?.id}
                    {...register(`guestlist_first_name`, {
                      required: true,
                      disabled: creatingLoading,
                    })}
                  />
                </Box>
                <Box>
                  <Text w="full">Last Name</Text>
                  <Input
                    placeholder="Name"
                    key={guestlistUser?.id}
                    {...register(`guestlist_last_name`, {
                      required: true,
                      disabled: creatingLoading,
                    })}
                  />
                </Box>
              </HStack>
              {isSystemTicketType ? null : (
                <Stack spacing={0} w="100%" zIndex={2}>
                  <Text w="full">Phone Number</Text>
                  <InputGroup>
                    <Controller
                      name={`phone_number`}
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (isSystemTicketType && !value) return true;

                          const phone = value?.includes(`+`)
                            ? value
                            : `+${value}`;
                          const isValid = isValidPhoneNumber(phone, {
                            defaultCountry: "US",
                            defaultCallingCode: "+1",
                          });

                          const lenghtValid = validatePhoneNumberLength(phone, {
                            defaultCountry: "US",
                            defaultCallingCode: "+1",
                          });

                          if (lenghtValid) {
                            return phoneErrors[lenghtValid];
                          }

                          if (isValid) {
                          }

                          return isValid ? true : `Invalid phone number`;
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          disabled={creatingLoading}
                          autoFormat={true}
                          inputProps={{
                            placeholder: "Phone Number",
                          }}
                          dropdownStyle={{ zIndex: 100 }}
                          inputStyle={{
                            zIndex: 999,
                            width: "100%",
                            height: "45px",
                            fontFamily: "Work sans",
                            borderWidth: "1px",
                            borderRadius: "7px",
                            borderColor: "rgba(0,0,0,0.1)",
                          }}
                          countryCodeEditable={false}
                          country={"us"}
                          onChange={onChange}
                          value={value}
                        />
                      )}
                    />
                  </InputGroup>
                  <ErrorMessage
                    name={`phone_number`}
                    errors={errors}
                    render={({ message }) => (
                      <Text pt={2} fontSize="sm" color="kydRed.200">
                        {message}
                      </Text>
                    )}
                  />
                </Stack>
              )}
              <Stack spacing={0} width="100%">
                <HStack w="full">
                  <Text>Tickets</Text>
                  {!isSystemTicketType ? (
                    <chakra.span fontSize={"xs"}>
                      (
                      {
                        allocation.allocations.find(
                          (tt) => tt.ticket_type_id === selectedTicketTypeId
                        )?.remaining
                      }
                      {` remaining`})
                    </chakra.span>
                  ) : null}
                </HStack>
                <Input
                  placeholder="quantity"
                  type="number"
                  disabled={isLoading}
                  isInvalid={!!errors.quantity?.message}
                  {...register(`quantity`, {
                    max: {
                      value: isSystemTicketType
                        ? undefined
                        : selectedAllocation?.remaining +
                          selectedAllocation?.assigned,
                      message: `Can't be more than the currently assigning tickets plus remaining tickets for ticket type. (${
                        selectedAllocation?.remaining +
                        selectedAllocation?.assigned
                      })`,
                    },
                    min: { value: 1, message: "Has to be at least 1." },

                    valueAsNumber: true,
                  })}
                />
                <ErrorMessage
                  name={`quantity`}
                  errors={errors}
                  render={({ message }) => (
                    <Text pt={2} fontSize="sm" color="kydRed.200">
                      {message}
                    </Text>
                  )}
                />
              </Stack>
              <VStack spacing={0} gap={0} w="full" display="none">
                <Text w="full">Ticket Type</Text>
                <Select
                  {...register(`ticket_type_id`)}
                  value={selectedTicketTypeId}
                  isDisabled={
                    allocationsWithAvailableTickets.length === 1 ||
                    (isSystemTicketType && isEdit)
                  }
                >
                  {allocationsWithAvailableTickets.map((tt) => (
                    <option
                      key={tt.ticket_type_id}
                      value={tt.ticket_type_id}
                      disabled={isTicketTypeSystem(tt.ticket_type_id) && isEdit}
                    >
                      {getTicketTypeName(tt.ticket_type_id)}
                    </option>
                  ))}
                </Select>
              </VStack>
              <VStack spacing={0} gap={0} w="full">
                <Text w="full">Notes</Text>
                <Textarea
                  noOfLines={3}
                  placeholder="Add here notes about this attendee"
                  disabled={isLoading}
                  {...register("notes")}
                />
              </VStack>
              <VStack spacing={0} gap={0} w="full">
                <Text w="full">Tags</Text>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <CreatableSelect
                      {...register("tags")}
                      placeholder={"Start writing to create tags"}
                      defaultValue={field.value?.map((v) => ({
                        label: v,
                        value: v,
                      }))}
                      isMulti
                      onChange={(value: { label: string; value: string }[]) => {
                        field.onChange(value.map((t) => t.value));
                      }}
                      chakraStyles={{
                        dropdownIndicator: (provided, state) => ({
                          ...provided,
                          background: "white",
                          w: "20px",
                        }),
                        container: (provided) => ({
                          ...provided,
                          width: "100%",
                          background: "white",
                          zIndex: 100,
                        }),

                        indicatorSeparator: (provided, state) => ({
                          ...provided,
                          display: "white",
                        }),
                      }}
                    />
                  )}
                />
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack direction={["column-reverse", "row-reverse"]} width="100%">
              <Button
                key={"cancel"}
                isLoading={isLoading}
                isDisabled={isLoading}
                w={"auto"}
                onClick={_onClose}
              >
                Cancel
              </Button>
              <Button
                key={"submit"}
                variant="kydDark"
                w={"auto"}
                isLoading={isLoading}
                isDisabled={isLoading || !isValid}
                onClick={handleSubmit((data) =>
                  onSave(data, isEdit ? "edit" : "create")
                )}
              >
                Save
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const withAddUserDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={GuestlistAddUserDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default GuestlistAddEditUserDialogMobile;
